import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import { TaskState } from '../../../common/constants/states';
import { NORA_DISPLAY_DATE_FORMAT } from '../../detail/utilities/formattingUtilities';
import { ITask, TaskUploadStatus } from '../interfaces/ITask';
import { Document } from '../types/document';
import { Evidence } from '../types/evidence';
import { TaskData } from '../types/task';
import {
  TaskCategory,
  TaskCategoryMapping,
  TaskTypes,
} from '../types/taskTypes';

export const DECISION_TASK_DEFAULT_ID = -2222;
export const OVERVIEW_TASK_DEFAULT_ID = -3333;
export const NOTES_TASK_DEFAULT_ID = -4444;
export const RULE_RESULTS_DEFAULT_ID = -5555;
export const PIA_TASK_DEFAULT_ID = -6666;
export const SUMMARY = -7777;
export const DOCTOR_INFO_DEFAULT_ID = -8888;

const defaultTasks: ITask[] = [
  {
    id: DECISION_TASK_DEFAULT_ID,
    taskType: TaskTypes.DECISION,
    taskData: null,
    state: TaskState.DEFAULT,
    outdated: false,
    retriedAt: '',
  },
  {
    id: OVERVIEW_TASK_DEFAULT_ID,
    taskType: TaskTypes.OVERVIEW,
    taskData: null,
    state: TaskState.DEFAULT,
    outdated: false,
    retriedAt: '',
  },
  {
    id: NOTES_TASK_DEFAULT_ID,
    taskType: TaskTypes.NOTES,
    taskData: null,
    state: TaskState.DEFAULT,
    outdated: false,
    retriedAt: '',
  },
  {
    id: DOCTOR_INFO_DEFAULT_ID,
    taskType: TaskTypes.DOCTOR_INFO,
    taskData: null,
    state: TaskState.DEFAULT,
    outdated: false,
    retriedAt: '',
  },
  {
    id: RULE_RESULTS_DEFAULT_ID,
    taskType: TaskTypes.RULE_RESULTS,
    taskData: null,
    state: TaskState.DEFAULT,
    outdated: false,
    retriedAt: '',
  },
];

const taskAdapter = createEntityAdapter<ITask>({
  selectId: (task: ITask) => task.id,
  sortComparer: (a: ITask, b: ITask) => {
    const aTaskCategory = TaskCategoryMapping[a.taskType];
    const bTaskCategory = TaskCategoryMapping[b.taskType];
    // Manual tasks - order in chronological order
    if (
      aTaskCategory === TaskCategory.MANUAL_TASK &&
      bTaskCategory === TaskCategory.MANUAL_TASK
    ) {
      // Sort in chronological order
      return moment(a.retriedAt, NORA_DISPLAY_DATE_FORMAT).diff(
        moment(b.retriedAt, NORA_DISPLAY_DATE_FORMAT),
      );
    }
    // Automated tasks - order by category order
    return aTaskCategory - bTaskCategory;
  },
});

export const taskListSlice = createSlice({
  name: 'taskList',
  initialState: taskAdapter.getInitialState({
    autoSendingTaskIds: [] as number[],
  }),
  reducers: {
    startAutoSend(state, action: PayloadAction<{ taskId: number }>) {
      if (!state.autoSendingTaskIds.includes(action.payload.taskId)) {
        state.autoSendingTaskIds.push(action.payload.taskId);
      }
    },
    endAutoSend(state, action: PayloadAction<{ taskId: number }>) {
      state.autoSendingTaskIds = state.autoSendingTaskIds.filter(
        id => id !== action.payload.taskId,
      );
    },
    addTask(state, action: PayloadAction<{ item: ITask }>) {
      taskAdapter.addOne(state, action.payload.item);
    },
    prependTask(state, action: PayloadAction<{ item: ITask }>) {
      const newItem = action.payload.item;
      const existingEntities = taskAdapter.getSelectors().selectAll(state);
      taskAdapter.setAll(state, [newItem, ...existingEntities]);
    },
    fetchTasksSuccess(state, action: PayloadAction<{ tasks: ITask[] }>) {
      taskAdapter.addMany(state, [...defaultTasks, ...action.payload.tasks]);
    },
    setTasks(state, action: PayloadAction<{ tasks: ITask[] }>) {
      taskAdapter.setAll(state, [...defaultTasks, ...action.payload.tasks]);
    },
    onTaskStateUpdateSuccess(
      state,
      action: PayloadAction<{
        id: number;
        state: TaskState;
        outdated: boolean;
        taskData?: TaskData;
        sufficient: boolean;
        feedback: string;
        waiveReason?: string | null;
      }>,
    ) {
      const update = {
        id: action.payload.id,
        changes: {
          state: action.payload.state,
          outdated: action.payload.outdated,
          sufficient: action.payload.sufficient,
          feedback: action.payload.feedback,
          waiveReason: action.payload.waiveReason ?? null,
        },
      };

      if (action.payload.taskData) {
        Object.assign(update.changes, { taskData: action.payload.taskData });
      }

      taskAdapter.updateOne(state, update);
    },
    clearTasks(state, _: PayloadAction<any>) {
      taskAdapter.removeAll(state);
    },
    clearTasksById(state, action: PayloadAction<{ id: number }>) {
      taskAdapter.removeOne(state, action.payload.id);
    },
    addEvidence(
      state,
      action: PayloadAction<{
        id: number;
        state: TaskState;
        evidence: Evidence;
        existingTask: ITask | undefined;
      }>,
    ) {
      if (action.payload.existingTask) {
        taskAdapter.updateOne(state, {
          id: action.payload.id,
          changes: {
            state: action.payload.state,
            outdated: false,
            evidence: transformEvidence(
              action.payload.existingTask,
              action.payload.evidence,
            ),
          },
        });
      }
    },
    deleteDocument(
      state,
      action: PayloadAction<{
        documentId: number;
        existingTask: ITask | undefined;
      }>,
    ) {
      if (action.payload.existingTask) {
        const { documentId, existingTask } = action.payload;
        if (existingTask.taskType === TaskTypes.DISPUTE) {
          taskAdapter.updateOne(state, {
            id: existingTask.id,
            changes: {
              attachments: (existingTask.attachments ?? []).map(doc =>
                doc.documentId === documentId
                  ? { ...doc, uri: '', deleted: true }
                  : doc,
              ),
            },
          });
        } else {
          const evidence = transformEvidence(existingTask, []);
          if (evidence.documents) {
            evidence.documents = evidence.documents.map(doc => {
              if (doc.documentId === documentId) {
                return {
                  uri: '',
                  documentType: doc.documentType,
                  documentSubType: doc.documentSubType,
                  fileName: doc.fileName,
                  createdAt: doc.createdAt,
                  deleted: true,
                  documentId: doc.documentId,
                };
              } else {
                return doc;
              }
            });
          }
          taskAdapter.updateOne(state, {
            id: existingTask.id,
            changes: { evidence },
          });
        }
      }
    },
    addAttachment: (state, action: PayloadAction<any>) => {
      const payload = action.payload;
      if (payload.documentType === 'DISPUTE') {
        const task = state.entities[payload.taskId];
        if (task) {
          taskAdapter.updateOne(state, {
            id: payload.taskId,
            changes: {
              attachments: [
                ...(task.attachments ?? []),
                transformDocument(payload),
              ],
            },
          });
        }
      } else {
        taskAdapter.updateOne(state, {
          id: payload.taskId,
          changes: {
            state: payload.taskState,
            attachment: transformDocument(payload),
          },
        });
      }
    },
    deleteAttachment: (state, action: PayloadAction<{ taskId: number }>) => {
      const payload = action.payload;
      taskAdapter.updateOne(state, {
        id: payload.taskId,
        changes: {
          attachment: undefined,
        },
      });
    },
    updateTaskState(
      state,
      action: PayloadAction<{
        id: number;
        state: TaskState;
        outdated: boolean;
        createdAt: string;
        retriedAt: string;
      }>,
    ) {
      taskAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          state: action.payload.state,
          outdated: action.payload.outdated,
          retriedAt: action.payload.retriedAt
            ? action.payload.retriedAt
            : action.payload.createdAt,
        },
      });
    },
    updateTaskUploadStatus(
      state,
      action: PayloadAction<{
        id: number;
        uploading: boolean;
        documentType: string;
        existingTaskData?: ITask;
      }>,
    ) {
      const taskData = action.payload.existingTaskData;
      const documentType = action.payload.documentType;
      const uploading = action.payload.uploading;
      let ongoingUploads: TaskUploadStatus = {};
      if (taskData && taskData.ongoingUpload) {
        ongoingUploads = Object.assign({}, taskData.ongoingUpload);
      }
      ongoingUploads[documentType] = uploading;
      taskAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          ongoingUpload: ongoingUploads,
        },
      });
    },
  },
});

const transformEvidence = (task: ITask, documentlist: any): Evidence => {
  const currentEvidence: Evidence = {} as Evidence;
  currentEvidence.documents = [];
  if (task.evidence) {
    currentEvidence.facts = task.evidence?.facts;
    currentEvidence.documents.push(...task.evidence.documents);
  }
  for (const documentItem of documentlist) {
    if (documentItem.status === 201) {
      const newDocument: Document = transformDocument(documentItem.document);
      currentEvidence.documents.push(newDocument);
    }
  }
  return currentEvidence;
};

const transformDocument = (data: any): Document => ({
  uri: data.uri,
  documentType: data.documentType,
  documentSubType: data.documentSubType,
  fileName: data.fileName,
  createdAt: data.createdAt,
  deleted: data.deleted,
  documentId: data.documentId,
});

export const taskListReducer = taskListSlice.reducer;
export const taskListActions = taskListSlice.actions;
export const { selectAll: getAllTasks } = taskAdapter.getSelectors();

/**
 * Categories that are not rendered as evidence rows on Detail and not shown
 * inline in the evidence list in the sidebar. Synthetic / section-anchor tasks
 * (Decision, Overview, Doctor Info, Rule Results, Notes) live here.
 */
const NON_EVIDENCE_CATEGORIES: ReadonlySet<TaskCategory> = new Set([
  TaskCategory.DEFAULT_TASK,
  TaskCategory.NOTES_TASK,
]);

export const getEvidenceTasks = (state: EntityState<ITask>) =>
  getAllTasks(state).filter(
    task => !NON_EVIDENCE_CATEGORIES.has(TaskCategoryMapping[task.taskType]),
  );
